import { styled } from "@mui/material/styles";
import { Button, Chip, DocumentIcon, focusRing, IconButton, LinkIcon, MenuItem, Meta, PaperclipIcon, PencilIcon, PlusIcon, Popover, RefreshIcon, SendIcon, Spinner, StopIcon, TextArea } from "@UI";
import { getIconFromType } from "@Utils/tree/getIconFromType";
import React from "react";
import { useTranslation } from "react-i18next";
import { ACCEPTED_ATTACHMENT_TYPES, MAX_ATTACHMENTS, useChatComposerStore } from "../store/chatComposerStore";
import { connectionKey, ConnectionsPicker } from "./ConnectionsPicker";
const ComposerDock = styled("form")({
    position: "relative",
    flexShrink: 0,
    padding: "var(--j-sp-2) var(--j-sp-3) var(--j-sp-3)",
    background: "var(--j-surface)",
    "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        top: "calc(-1 * var(--j-sp-4))",
        height: "var(--j-sp-4)",
        background: "linear-gradient(rgb(var(--j-surface-rgb) / 0), var(--j-surface))",
        pointerEvents: "none"
    }
});
const Shell = styled("div")({
    background: "var(--j-surface)",
    border: "1px solid var(--j-line-strong)",
    borderRadius: "var(--j-r-md)",
    overflow: "hidden",
    transition: "border-color var(--j-dur-fade) var(--j-ease)",
    "&:focus-within": {
        borderColor: "var(--j-accent)"
    },
    "&:has(.question-dock:focus-within)": {
        borderColor: "var(--j-line-strong)"
    },
    "&[data-dragging=true]": {
        borderColor: "var(--j-accent)",
        borderStyle: "dashed",
        background: "var(--j-accent-soft)"
    }
});
const Chips = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    gap: "var(--j-sp-1)",
    padding: "var(--j-sp-2) var(--j-sp-2) 0"
});
const AttachmentChip = styled(Chip)({
    "&.is-failed": {
        color: "var(--j-err)"
    }
});
const RetryButton = styled("button")({
    ...focusRing,
    display: "grid",
    placeItems: "center",
    padding: 0,
    border: 0,
    borderRadius: "var(--j-r-xs)",
    background: "none",
    color: "inherit",
    cursor: "pointer",
    "&:hover": {
        background: "var(--j-line)"
    }
});
const FileInput = styled("input")({
    display: "none"
});
const Notice = styled("div")({
    padding: "0 var(--j-sp-3)",
    fontSize: "var(--j-fs-sm)",
    lineHeight: "var(--j-lh-sm)",
    color: "var(--j-ink-2)",
    "&[data-error=true]": {
        color: "var(--j-err)"
    }
});
const Field = styled(TextArea)({
    border: 0,
    borderRadius: 0,
    minHeight: "2.75rem",
    padding: "var(--j-sp-2) var(--j-sp-3) var(--j-sp-1)",
    background: "transparent",
    color: "var(--j-ink)",
    fontSize: "var(--j-fs-md)",
    lineHeight: "var(--j-lh-lg)",
    "&::placeholder": {
        color: "var(--j-ink-3)"
    },
    "&:focus, &:focus-visible": {
        outline: "none",
        boxShadow: "none"
    }
});
const Tools = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-0-5)",
    padding: "var(--j-sp-1)",
    "& > .composer-meta": {
        flex: 1,
        minWidth: 0,
        paddingLeft: "var(--j-sp-1)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
    }
});
const ConnectButton = styled(IconButton)({
    position: "relative",
    "&[data-active=true]": {
        background: "var(--j-accent-soft)",
        color: "var(--j-accent-ink)"
    },
    "& > .badge": {
        position: "absolute",
        top: -3,
        right: -3,
        minWidth: 14,
        height: 14,
        padding: "0 3px",
        borderRadius: "var(--j-r-lg)",
        display: "grid",
        placeItems: "center",
        background: "var(--j-accent)",
        color: "var(--j-on-accent)",
        fontSize: "var(--j-fs-2xs)",
        fontWeight: 600,
        lineHeight: 1,
        boxShadow: "0 0 0 2px var(--j-surface)"
    }
});
const ModeButton = styled(Button)({
    flexShrink: 0,
    height: 28,
    padding: "0 var(--j-sp-2)",
    fontSize: "var(--j-fs-sm)",
    "&[data-active=true]": {
        background: "var(--j-accent-soft)",
        color: "var(--j-accent-ink)"
    }
});
const SendButton = styled(IconButton)({
    marginLeft: "auto",
    borderRadius: "var(--j-r-sm)",
    background: "var(--j-accent)",
    color: "var(--j-on-accent)",
    "&:hover:not(:disabled)": {
        background: "var(--j-accent-hover)"
    },
    "&:disabled": {
        opacity: 1,
        background: "var(--j-well)",
        color: "var(--j-ink-3)"
    }
});
export function Input({ value, onChange, onSend, onStop, isResponding, disabled, placeholder, meta, notice, inputRef, dock }) {
    const { t } = useTranslation();
    const [isPickerOpen, setIsPickerOpen] = React.useState(false);
    const [addMenuAnchor, setAddMenuAnchor] = React.useState(null);
    const connections = useChatComposerStore((s)=>s.connections);
    const setConnections = useChatComposerStore((s)=>s.setConnections);
    const isAutoApply = useChatComposerStore((s)=>s.wordApplyMode === "auto");
    const setWordApplyMode = useChatComposerStore((s)=>s.setWordApplyMode);
    const attachments = useChatComposerStore((s)=>s.attachments);
    const addFiles = useChatComposerStore((s)=>s.addFiles);
    const retryAttachment = useChatComposerStore((s)=>s.retryAttachment);
    const removeAttachment = useChatComposerStore((s)=>s.removeAttachment);
    const fileInputRef = React.useRef(null);
    const [refusal, setRefusal] = React.useState(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const count = connections.length;
    const canAttach = !disabled && attachments.length < MAX_ATTACHMENTS;
    const shownNotice = refusal ? {
        text: t(`chat.attachments.refused.${refusal}`, {
            count: MAX_ATTACHMENTS
        }),
        isError: true
    } : notice;
    const attach = (files)=>{
        if (files.length > 0) setRefusal(addFiles(files));
    };
    const hasFiles = (e)=>e.dataTransfer.types.includes("Files");
    const submit = ()=>{
        const text = value.trim();
        if (!text || disabled || !onSend(text)) return;
        setRefusal(null);
        onChange("");
    };
    const attachmentIcon = (attachment)=>{
        if (attachment.status === "uploading") return /*#__PURE__*/ React.createElement(Spinner, {
            size: "sm"
        });
        if (attachment.status === "done") return /*#__PURE__*/ React.createElement(DocumentIcon, {
            size: 12
        });
        return /*#__PURE__*/ React.createElement(RetryButton, {
            type: "button",
            "aria-label": t("chat.attachments.retry", {
                name: attachment.file.name
            }),
            onClick: ()=>retryAttachment(attachment.id)
        }, /*#__PURE__*/ React.createElement(RefreshIcon, {
            size: 12
        }));
    };
    const onKeyDown = (e)=>{
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && e.nativeEvent.keyCode !== 229) {
            e.preventDefault();
            submit();
        }
    };
    return /*#__PURE__*/ React.createElement(ComposerDock, {
        onSubmit: (e)=>{
            e.preventDefault();
            submit();
        },
        onDragOver: (e)=>{
            if (!hasFiles(e) || !canAttach) return;
            e.preventDefault();
            setIsDragging(true);
        },
        onDragLeave: (e)=>{
            if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
        },
        onDrop: (e)=>{
            if (!hasFiles(e)) return;
            e.preventDefault();
            setIsDragging(false);
            if (canAttach) attach(Array.from(e.dataTransfer.files));
        }
    }, /*#__PURE__*/ React.createElement(Shell, {
        "data-dragging": isDragging
    }, dock, (count > 0 || attachments.length > 0) && /*#__PURE__*/ React.createElement(Chips, null, attachments.map((attachment)=>/*#__PURE__*/ React.createElement(AttachmentChip, {
            key: attachment.id,
            tone: "filled",
            className: attachment.status === "failed" ? "is-failed" : undefined,
            icon: attachmentIcon(attachment),
            removeLabel: t("chat.attachments.remove", {
                name: attachment.file.name
            }),
            onRemove: ()=>removeAttachment(attachment.id)
        }, attachment.status === "failed" ? t("chat.attachments.failed", {
            name: attachment.file.name
        }) : attachment.file.name)), connections.map((connection)=>{
        const key = connectionKey(connection);
        const Icon = getIconFromType(connection.type, connection.isAbstract);
        const title = connection.title ?? t("chat.connections.fallback");
        return /*#__PURE__*/ React.createElement(Chip, {
            key: key,
            tone: "filled",
            icon: Icon ? /*#__PURE__*/ React.createElement(Icon, {
                size: 12
            }) : undefined,
            removeLabel: t("chat.connections.remove", {
                title
            }),
            onRemove: ()=>setConnections(connections.filter((item)=>connectionKey(item) !== key))
        }, title);
    })), /*#__PURE__*/ React.createElement(Field, {
        ref: inputRef,
        minRows: 1,
        maxRows: 8,
        value: value,
        placeholder: placeholder ?? t("chat.composer.placeholder"),
        "aria-label": t("chat.composer.label"),
        "data-testid": "composer-input",
        onChange: (e)=>{
            setRefusal(null);
            onChange(e.target.value);
        },
        onKeyDown: onKeyDown,
        disabled: disabled
    }), /*#__PURE__*/ React.createElement(Notice, {
        role: "status",
        "aria-live": "polite",
        "data-error": !!shownNotice?.isError,
        hidden: !shownNotice
    }, shownNotice?.text), /*#__PURE__*/ React.createElement(Tools, null, /*#__PURE__*/ React.createElement(ConnectButton, {
        "aria-label": t("chat.composer.add"),
        size: "sm",
        "aria-haspopup": "menu",
        "aria-expanded": !!addMenuAnchor,
        "data-active": count > 0,
        disabled: disabled,
        onClick: (e)=>setAddMenuAnchor(addMenuAnchor ? null : e.currentTarget)
    }, /*#__PURE__*/ React.createElement(PlusIcon, {
        size: 16
    }), count > 0 && /*#__PURE__*/ React.createElement("span", {
        className: "badge",
        "aria-hidden": true
    }, count)), /*#__PURE__*/ React.createElement(Popover, {
        open: !!addMenuAnchor,
        anchorEl: addMenuAnchor,
        onClose: ()=>setAddMenuAnchor(null),
        placement: "top-start",
        width: "auto",
        "aria-label": t("chat.composer.add")
    }, /*#__PURE__*/ React.createElement(MenuItem, {
        icon: /*#__PURE__*/ React.createElement(PaperclipIcon, {
            size: 14
        }),
        disabled: !canAttach,
        onClick: ()=>{
            setAddMenuAnchor(null);
            fileInputRef.current?.click();
        }
    }, t("chat.attachments.add")), /*#__PURE__*/ React.createElement(MenuItem, {
        icon: /*#__PURE__*/ React.createElement(LinkIcon, {
            size: 14
        }),
        trailing: count > 0 && /*#__PURE__*/ React.createElement(Meta, null, count),
        onClick: ()=>{
            setAddMenuAnchor(null);
            setIsPickerOpen(true);
        }
    }, t("chat.connections.link"))), /*#__PURE__*/ React.createElement(FileInput, {
        ref: fileInputRef,
        type: "file",
        multiple: true,
        accept: ACCEPTED_ATTACHMENT_TYPES.map((type)=>`.${type}`).join(","),
        onChange: (e)=>{
            attach(Array.from(e.target.files ?? []));
            e.target.value = "";
        }
    }), /*#__PURE__*/ React.createElement(ModeButton, {
        variant: "ghost",
        size: "sm",
        "data-active": isAutoApply,
        startIcon: /*#__PURE__*/ React.createElement(PencilIcon, {
            size: 12
        }),
        onClick: ()=>setWordApplyMode(isAutoApply ? "review" : "auto")
    }, isAutoApply ? t("chat.exec.modeAuto") : t("chat.exec.modeReview")), /*#__PURE__*/ React.createElement(Meta, {
        className: "composer-meta",
        "aria-live": "polite"
    }, meta), isResponding ? /*#__PURE__*/ React.createElement(SendButton, {
        size: "sm",
        "aria-label": t("chat.composer.stop"),
        "data-testid": "composer-stop",
        onClick: onStop
    }, /*#__PURE__*/ React.createElement(StopIcon, {
        size: 10
    })) : /*#__PURE__*/ React.createElement(SendButton, {
        size: "sm",
        type: "submit",
        "aria-label": t("chat.composer.send"),
        "data-testid": "composer-send",
        disabled: !value.trim() || disabled
    }, /*#__PURE__*/ React.createElement(SendIcon, {
        size: 14
    })))), /*#__PURE__*/ React.createElement(ConnectionsPicker, {
        open: isPickerOpen,
        onClose: ()=>setIsPickerOpen(false)
    }));
}
